/* eslint-disable no-nested-ternary, max-lines */
import React, { useEffect, useImperativeHandle } from "react";
import { Card, Accordion, Row, Col } from "react-bootstrap";
import { useParams, useSearchParams } from "react-router-dom";
import { useFormContext, get } from "react-hook-form";
import { FormattedMessage } from "react-intl";

import { createQuotationDraft } from "api";
import { useGetQuotationFormConfig } from "api/queries/quotation-form/useGetQuotationFormConfig";
import { ReactComponent as SortIcon } from "assets/images/icons/SortDown.svg";
import { ReactComponent as CheckIcon } from "assets/images/icons/Check.svg";
import { ReactComponent as ExclamationIcon } from "assets/images/icons/Exclamation.svg";
import { QuotationFormStepTypes, QuotationFormStepValidationTypes } from "constants/";
import FreightModeButton from "components/organisms/FreightModeButton/FreightModeButton";
import { useGetQuoteRequestDetails } from "api/queries/quotes/useGetQuoteRequestDetails";
import { QuotationFreightModeOption } from "types/Common";
import ServiceFormSidebar from "./ServiceFormSidebar";
import ServiceFormInputs from "./ServiceFormInputs";
import "./ServiceForm.scss";
import { QuotationFormState } from "../../QuotationFormState";

type ServiceFormProps = {
  childRef: React.MutableRefObject<any>;
  state: QuotationFormState;
};

function ServiceForm({ childRef, state }: ServiceFormProps) {
  const {
    clearErrors,
    formState: { errors },
    getValues,
    setError,
    setValue,
    watch,
  } = useFormContext();

  const id = useParams().id?.toUpperCase();
  const { data: quotationConfigData } = useGetQuotationFormConfig(id!, {
    enabled: !!id,
    refetchOnMount: false,
  });

  const { data: quoteRequestDetails } = useGetQuoteRequestDetails({
    shipmentId: id!,
    options: {
      enabled: !!id,
      refetchOnMount: false,
    },
  });

  const [searchParams, setSearchParams] = useSearchParams();
  const currentActiveStep = searchParams.get("active_step");
  const draftId = searchParams.get("draft_id");

  const freightModeOptions: QuotationFreightModeOption[] =
    quotationConfigData?.freight_modes.map((mode: any) => ({ ...mode, value: mode.value.toLowerCase() })) ?? [];

  const selectedFreightMode = watch("service.freight_mode");
  const selectedFreightModeObject = freightModeOptions.find((mode) => mode.value === watch("service.freight_mode"));

  const serviceFields = QuotationFormStepValidationTypes[QuotationFormStepTypes.SERVICE] as string[];
  const hasError = serviceFields.some((item) => get(errors, item));

  useEffect(() => {
    // Create draft once selectedFreightMode has been picked
    if (!draftId && id && selectedFreightMode) {
      createQuotationDraft(id, { freight_mode: selectedFreightMode }).then((res: any) => {
        searchParams.set("draft_id", res.data.reference_number);
        setSearchParams(searchParams);
      });
    }
  }, [draftId, id, selectedFreightMode]);

  useImperativeHandle(childRef, () => ({ checkStepValidation }));

  const checkStepValidation = () => {
    if (!getValues("service.freight_mode")) {
      setError("service.freight_mode", {
        type: "custom",
        message: "Please select a freight mode.",
      });
      return false;
    }
    return true;
  };

  const handleSelectFreightMode = (freightMode: QuotationFreightModeOption) => () => {
    setValue("service.freight_mode", freightMode.value);
    clearErrors("service.freight_mode");
  };

  return (
    <Card className="mb-3 form-container">
      <Accordion.Toggle as={Card.Header} className="accordion-header" eventKey={QuotationFormStepTypes.SERVICE}>
        <div className="d-flex align-items-center">
          {(hasError || state.draftSaved[QuotationFormStepTypes.SERVICE]) && (
            <div className={`valid-status ${hasError ? "error" : ""}`}>{hasError ? <ExclamationIcon /> : <CheckIcon />}</div>
          )}
          <h5>
            <FormattedMessage id="quote-request.service" defaultMessage="Service" />
          </h5>
        </div>
        <SortIcon
          fill="#000"
          width={8}
          height={7}
          transform={`rotate(${currentActiveStep === QuotationFormStepTypes.SERVICE ? 180 : 0})`}
        />
      </Accordion.Toggle>
      <Accordion.Collapse eventKey={QuotationFormStepTypes.SERVICE}>
        <Card.Body>
          <Row className="mb-24">
            <Col lg={2} md={3}>
              <ServiceFormSidebar
                selectedFreightModeObject={selectedFreightModeObject}
                quoteRequestDetails={quoteRequestDetails}
              />
            </Col>
            <Col>
              {!freightModeOptions.length ? (
                <div className="skeleton-card">
                  <Row className="skeleton-row">
                    <span key={6} className="skeleton full" />
                  </Row>
                </div>
              ) : (
                <>
                  <div className="mb-3">
                    <div className="d-flex flex-wrap">
                      {freightModeOptions.map((freightMode) => {
                        const isSelected = freightMode.value === selectedFreightMode;
                        return (
                          <FreightModeButton
                            key={freightMode.value}
                            isSelected={isSelected}
                            onClick={handleSelectFreightMode(freightMode)}
                            value={freightMode.label}
                            type={freightMode.type}
                            modeValue={freightMode.value}
                          />
                        );
                      })}
                      {errors?.service?.freight_mode && (
                        <p className="d-block font-b3 invalid-feedback">{errors?.service?.freight_mode?.message}</p>
                      )}
                    </div>
                  </div>
                  <ServiceFormInputs
                    selectedFreightModeObject={selectedFreightModeObject}
                    quoteRequestDetails={quoteRequestDetails}
                  />
                </>
              )}
            </Col>
          </Row>
        </Card.Body>
      </Accordion.Collapse>
    </Card>
  );
}

export default ServiceForm;
